import { type CSSProperties, useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { setLocale } from '@/i18n';
import { LOCALE_AUTONYMS, LOCALE_SHORT, SUPPORTED_LOCALES, type Locale } from '@/i18n/locale';
import { haptics } from '@/lib/native';

// Decorative — the pill icon. A glyph, not language (rendered via const so the
// no-literals guard stays happy and translators never see it).
const GLOBE_GLYPH = '🌐';

function currentLocale(): Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as Locale)
    : 'en';
}

/**
 * Home-header language switcher (SST-23, Phase 6). A compact pill showing the
 * active language code; tapping opens a menu of the four locales. Selecting one
 * persists the choice (Preferences) and switches the app live via setLocale().
 * Autonyms come from consts, never the catalog — a language name is shown the
 * same in every locale.
 */
export function LanguageSelector() {
  const { t } = useTranslation(); // re-renders this component on changeLanguage
  const [open, setOpen] = useState(false);
  const current = currentLocale();

  // Dismiss on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function choose(loc: Locale) {
    haptics.light();
    void setLocale(loc);
    setOpen(false);
  }

  return (
    <div style={wrap}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('common:language')}
        onClick={() => {
          haptics.light();
          setOpen((o) => !o);
        }}
        style={pill}
      >
        <span aria-hidden="true">{GLOBE_GLYPH}</span>
        <span>{LOCALE_SHORT[current]}</span>
      </button>

      {open && (
        <>
          {/* Full-screen dismiss layer. A button (interactive) so it's a11y-clean. */}
          <button type="button" aria-label={t('common:close')} onClick={() => setOpen(false)} style={backdrop} />
          <div role="menu" style={menu}>
            {SUPPORTED_LOCALES.map((loc) => (
              <button
                key={loc}
                type="button"
                role="menuitem"
                aria-current={loc === current}
                onClick={() => choose(loc)}
                style={{ ...item, fontWeight: loc === current ? 700 : 500 }}
              >
                <span>{LOCALE_AUTONYMS[loc]}</span>
                {loc === current && <span aria-hidden="true">{t('common:check')}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const wrap: CSSProperties = { position: 'relative' };

const pill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.3,
  padding: '6px 10px',
  borderRadius: 999,
  border: '1px solid rgba(0,0,0,0.10)',
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(8px)',
  color: '#0a0a0a',
  cursor: 'pointer',
};

const backdrop: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 40,
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'default',
};

const menu: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  right: 0,
  zIndex: 41,
  minWidth: 150,
  background: '#fff',
  borderRadius: 12,
  border: '1px solid rgba(0,0,0,0.08)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
  overflow: 'hidden',
  padding: 4,
};

const item: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  width: '100%',
  textAlign: 'left',
  fontSize: 15,
  color: '#0a0a0a',
  background: 'transparent',
  border: 'none',
  borderRadius: 8,
  padding: '10px 12px',
  cursor: 'pointer',
};
